import { Injectable, type Signal, signal } from '@angular/core';
import type { DiagramEditor } from '@bizzdesign/modeler/canvazz';
import {
  type ActionEventConfig,
  type DiagramPayloadCanvazz,
  type GenericObject,
  type GraphicImagePairWithData,
  type ImageStatus,
  ImageStatuses,
  type MetamodelMetadata,
  SupportedMetamodels,
} from '@bizzdesign/modeler/models';
import type {
  RepoLoadedElementDataResponse,
  RepositoryLoadedElementData,
} from '@bizzdesign/shared/models';
import { Subject } from 'rxjs';
import {
  hasInternalDiagram,
  isGraphicWithImageData,
} from '../utils/editor-internals.utils';

@Injectable()
export class ModelerEditorService {
  private readonly _editor = signal<DiagramEditor | null>(null);
  readonly imageLoadStateChanged$ = new Subject<void>();
  readonly editor: Signal<DiagramEditor | null> = this._editor.asReadonly();

  setEditor(editor: DiagramEditor | null): void {
    this._editor.set(editor);
  }

  getSnapshot(
    width?: number,
    height?: number,
    asSVG?: boolean,
  ): Promise<string | undefined> {
    const editor = this.editor();

    return editor
      ? editor.exportAsImage(width ?? null, height ?? null, asSVG ?? true)
      : Promise.resolve(undefined);
  }

  /**
   * Reports whether the current frame contains images that are still loading.
   *
   * @returns `true` while at least one server-backed image is loading.
   */
  hasPendingImageLoads(): boolean {
    const editor = this.editor();
    if (!editor || !hasInternalDiagram(editor)) return false;
    return (editor.diagram?.entityGraphics ?? []).some((graphic) => {
      if (!isGraphicWithImageData(graphic)) return false;
      const status = graphic.customStyle?.imageMetadata?.status;
      return status === ImageStatuses.Loading;
    });
  }

  getMetamodelMetadata(): MetamodelMetadata {
    const metamodelMetadata = this.editor()?.getMetamodelMetadata();
    if (!metamodelMetadata?.currentMetamodelName) {
      return {
        currentMetamodelName: SupportedMetamodels.FREEFORMAT,
        targetMetamodelName: undefined,
      };
    }

    return metamodelMetadata;
  }

  getFrameImageIds(): string[] {
    const editor = this.editor();
    if (!editor || !hasInternalDiagram(editor)) return [];
    const graphics = editor.diagram?.entityGraphics ?? [];
    return [
      ...new Set(
        graphics
          .map((g) =>
            isGraphicWithImageData(g)
              ? g.customStyle?.imageMetadata?.imageId
              : undefined,
          )
          .filter((id): id is string => typeof id === 'string'),
      ),
    ];
  }

  /**
   * Returns image graphics with loaded image data from metadata or the editor image cache.
   */
  getLoadedImageGraphics(): Array<{
    graphicId: string;
    imageId?: string;
    imageSrc: string;
    description?: string;
    alt?: string;
  }> {
    const editor = this.editor();
    if (!editor || !hasInternalDiagram(editor)) return [];
    const graphics = editor.diagram?.entityGraphics ?? [];
    return graphics.flatMap((g) => {
      if (!isGraphicWithImageData(g)) return [];
      const imageMetadata = g.customStyle?.imageMetadata;
      const imageId = imageMetadata?.imageId;
      const cachedImageSrc =
        typeof imageId === 'string'
          ? editor.environment?.getImageSrc?.(imageId)
          : undefined;
      const cachedImageMetadata =
        typeof imageId === 'string'
          ? editor.environment?.getImageMetadata?.(imageId)
          : undefined;
      const imageSrc = cachedImageSrc ?? imageMetadata?.imageSrc;
      if (
        typeof imageSrc !== 'string' ||
        imageSrc.length === 0 ||
        typeof g.id !== 'string'
      ) {
        return [];
      }
      return [
        {
          graphicId: g.id,
          imageSrc,
          ...(typeof imageId === 'string' ? { imageId } : {}),
          ...(cachedImageMetadata === undefined ? {} : cachedImageMetadata),
        },
      ];
    });
  }

  getCurrentModelData(): GenericObject[] {
    return this.editor()?.getCurrentModelData() ?? [];
  }

  getFreeFormatDataFromCurrentModelData(): GenericObject[] {
    return this.editor()?.getFreeFormatDataFromCurrentModelData() ?? [];
  }

  hasEntityElements(): boolean {
    // using a simpler method to just check the number of entities,
    // without any conversions or data fetching
    return !!this.editor()?.hasEntityElements();
  }

  updateDiagramPayload(
    value: Partial<DiagramPayloadCanvazz>,
    eventName?: string,
  ): void {
    const { payload, ...metadata } = value;
    const { diagramPayloadMetaSync, diagramPayloadDataSync } =
      this.editor()?.collaborationManager || {};

    diagramPayloadMetaSync?.updatePayloadMeta(metadata, eventName);

    if (payload) {
      diagramPayloadDataSync?.overridePayloadData(payload);
    }
  }

  validatePayload(payload: GenericObject | null): string[] | undefined {
    if (!payload) return undefined;
    return this.editor()?.validatePayload(payload);
  }

  updateFromRepoElements(fromRepoElements: string[]): void {
    const { diagramPayloadFromRepoSync } =
      this.editor()?.collaborationManager || {};

    if (!diagramPayloadFromRepoSync) return;

    diagramPayloadFromRepoSync.addFromRepoElements(fromRepoElements);
  }

  setFromRepoElementsFromDraftData(fromRepoElements: string[] | null): void {
    if (fromRepoElements === null) return;
    this.editor()?.setFromRepoElementsFromDraftData(fromRepoElements);
  }

  setDeletedFromRepoElementsFromDraftData(
    deletedFromRepoElements: string[] | null,
  ): void {
    if (deletedFromRepoElements === null) return;
    this.editor()?.setDeletedFromRepoElementsFromDraftData(
      deletedFromRepoElements,
    );
  }

  updateDataFromRepoElements(
    changedElements: RepositoryLoadedElementData[],
  ): void {
    if (!changedElements.length) return;
    this.editor()?.updateDataFromRepoElements(changedElements);
  }

  updateDataFromRepoFromDrawer(
    changedElement: RepoLoadedElementDataResponse,
  ): void {
    this.editor()?.updateDataFromRepoFromDrawer(changedElement);
  }

  setImageMetadataFromDeliverableForGraphic(
    graphicId: string,
    imageId: string,
    alt?: string,
  ): void {
    this.editor()?.conversionManager?.updateImageMetadata(graphicId, imageId, {
      alt,
    });
    this.imageLoadStateChanged$.next();
  }

  setImageMetadataFromDeliverableForGraphics(
    data: GraphicImagePairWithData[],
  ): void {
    this.editor()?.conversionManager?.updateImageMetadataForGraphics(data);
    this.imageLoadStateChanged$.next();
  }

  updateImageMetadataBatch(updates: GraphicImagePairWithData[]): void {
    this.editor()?.conversionManager?.updateImageMetadataBatch(updates);
    this.imageLoadStateChanged$.next();
  }

  updateImageStatusBatch(graphicIds: string[], status: ImageStatus): void {
    this.editor()?.conversionManager?.updateImageStatusBatch(
      graphicIds,
      status,
    );
    this.imageLoadStateChanged$.next();
  }

  sendEventToModeler(eventConfig: ActionEventConfig): void {
    const editor = this.editor();
    if (!editor) return;
    editor.handleToolbarEvent(eventConfig);
  }

  getEligibleElementsForGrouping(ids: string[]): GenericObject[] {
    return this.editor()?.getEligibleElementsForGrouping(ids) ?? [];
  }

  /**
   * Expands any group in the given selection into the members a bulk action applies to.
   *
   * @param ids - The graphic ids of the current selection.
   * @returns The graphics a bulk action should act on, empty when no editor is attached.
   */
  getGroupMembers(ids: string[]): GenericObject[] {
    return this.editor()?.getGroupMembers(ids) ?? [];
  }

  /**
   * Resolves the member whose style the toolbar displays for a selected group: its first child,
   * descending into nested groups.
   *
   * @param groupId - The graphic id of the selected group.
   * @returns The first member's payload, or `undefined` when there is no editor, the id is not a
   *   group, or the group is empty.
   */
  getGroupFirstMember(groupId: string): GenericObject | undefined {
    return this.editor()?.getGroupFirstMember(groupId);
  }
}
